import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Stamp, Inbox, CalendarClock, BadgeEuro, X, Clock3, PenLine, RefreshCw, AlarmClockOff } from "lucide-react";
import { api } from "../api";

const ICONS = { aprovacao: Stamp, lead: Inbox, fatura_vencer: CalendarClock, pagamento: BadgeEuro,
  task_prazo: Clock3, assinatura_pendente: PenLine, renovacao_pronta: RefreshCw, tarefa_hora_passada: AlarmClockOff };
const STYLES = {
  aprovacao: "bg-amber-50 text-amber-600",
  lead: "bg-tint text-blue",
  fatura_vencer: "bg-red-50 text-red-600",
  pagamento: "bg-emerald-50 text-emerald-600",
  task_prazo: "bg-orange-50 text-orange-600",
  assinatura_pendente: "bg-indigo-50 text-indigo-600",
  renovacao_pronta: "bg-emerald-50 text-emerald-700",
  tarefa_hora_passada: "bg-red-50 text-red-600",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `ha ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `ha ${Math.floor(diff / 3600)} h`;
  return `ha ${Math.floor(diff / 86400)} d`;
}

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    o.start(); o.stop(ctx.currentTime + 0.42);
    o.onended = () => ctx.close();
  } catch (e) { /* noop */ }
}

export const NotificationBell = () => {
  const [data, setData] = useState({ total: 0, nao_vistas: 0, items: [] });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  const lastLeadEm = useRef(null);
  const initialized = useRef(false);
  const baseTitle = useRef(typeof document !== "undefined" ? document.title.replace(/^\(\d+\)\s*/, "") : "SaaS-Hub");
  const flashTimer = useRef(null);

  const load = () => api.get("/notifications").then((r) => {
    const d = r.data;
    const leads = d.items.filter((i) => i.tipo === "lead");
    const newest = leads.length ? leads[0].em : null; // items ja vem ordenado desc
    if (initialized.current && newest && newest > (lastLeadEm.current || "")) {
      beep();
      // Piscar titulo do separador para reacao mesmo em segundo plano
      let on = false;
      clearInterval(flashTimer.current);
      flashTimer.current = setInterval(() => {
        document.title = on ? baseTitle.current : "Nova lead! - " + baseTitle.current;
        on = !on;
      }, 1000);
      setTimeout(() => { clearInterval(flashTimer.current); applyTitle(d.nao_vistas); }, 8000);
    }
    if (newest) lastLeadEm.current = newest;
    initialized.current = true;
    setData(d);
    if (!flashTimer.current) applyTitle(d.nao_vistas);
  }).catch(() => {});

  const applyTitle = (n) => {
    if (typeof document === "undefined") return;
    document.title = n > 0 ? `(${n}) ${baseTitle.current}` : baseTitle.current;
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onFocus = () => { clearInterval(flashTimer.current); flashTimer.current = null; applyTitle(0); };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); clearInterval(flashTimer.current); document.removeEventListener("mousedown", onClick); window.removeEventListener("focus", onFocus); };
  }, []); // eslint-disable-line

  useEffect(() => { if (!flashTimer.current) applyTitle(data.nao_vistas); }, [data.nao_vistas]); // eslint-disable-line

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.nao_vistas > 0) {
      try { await api.post("/notifications/seen"); setData((d) => ({ ...d, nao_vistas: 0 })); applyTitle(0); } catch (e) { /* noop */ }
    }
  };

  const go = (link) => { setOpen(false); nav(link); };

  const dismiss = async (e, it) => {
    e.stopPropagation();
    e.preventDefault();
    if (!it?.id) return;
    // Actualiza optimistically
    setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== it.id), total: Math.max(0, d.total - 1) }));
    try { await api.post("/notifications/dismiss", { id: it.id }); }
    catch (err) { load(); /* reverte se falhar */ }
  };

  const dismissAll = async (e) => {
    e.stopPropagation();
    const n = data.items.length;
    if (!n) return;
    setData((d) => ({ ...d, items: [], total: 0, nao_vistas: 0 }));
    applyTitle(0);
    try { await api.post("/notifications/dismiss-all"); }
    catch (err) { load(); }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell"
        className="relative w-10 h-10 rounded-full bg-white border border-tint shadow-sm flex items-center justify-center text-navy hover:bg-tint transition-colors">
        <Bell size={18} />
        {data.nao_vistas > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {data.nao_vistas > 9 ? "9+" : data.nao_vistas}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-[440px] max-w-[92vw] bg-white border border-tint rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-tint flex items-center justify-between">
            <span className="font-bold text-navy text-sm">Notificacoes</span>
            <div className="flex items-center gap-2">
              {data.items.length > 0 && (
                <button onClick={dismissAll} data-testid="notif-dismiss-all"
                  className="text-[11px] font-semibold text-slate-500 hover:text-red-600 hover:underline">
                  Limpar todas
                </button>
              )}
              <span className="text-xs text-muted2">{data.total}</span>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted2">Sem notificacoes.</div>}
            {data.items.map((it, i) => {
              const Icon = ICONS[it.tipo] || Bell;
              return (
                <div key={it.id || i} data-testid={`notif-item-${i}`}
                  className="group relative w-full flex items-start gap-3 hover:bg-tint transition-colors border-b border-tint/60 last:border-0">
                  <button onClick={() => go(it.link)} className="flex-1 min-w-0 text-left px-4 py-3 flex items-start gap-3">
                    <span className={`mt-0.5 shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${STYLES[it.tipo] || "bg-tint text-blue"}`}><Icon size={15} /></span>
                    <span className="flex-1 min-w-0 pr-6">
                      <span className="block text-sm text-navy leading-snug break-words">{it.titulo}</span>
                      <span className="block text-[11px] text-muted2 mt-0.5">{timeAgo(it.em)}</span>
                    </span>
                  </button>
                  <button onClick={(e) => dismiss(e, it)}
                    data-testid={`notif-dismiss-${i}`}
                    title="Limpar notificacao"
                    className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all">
                    <X size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
